import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Callout,
  Card,
  DataList,
  DataListItem,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Heading,
  Label,
  Progress,
  Text,
} from '@/components/ui';
import {
  IconAlertTriangle,
  IconCheck,
  IconCloudUpload,
  IconFilter,
  IconList,
  IconRefresh,
} from '@tabler/icons-react';
import {
  FileProgressItem,
  FileProgressList,
} from '@/components/file-progress-list';
import {
  FileUploadStatus,
  StudyFilesUploadConfirmationModalProps,
} from './types';
import { formatFileSize } from '@/utils/fileUtils.ts';

const formatFileCount = (count: number): string => {
  return `${count} ${count === 1 ? 'file' : 'files'}`;
};

const mapUploadStatusToOperationStatus = (
  status: FileUploadStatus
): FileProgressItem['status'] => {
  switch (status) {
    case 'uploading':
      return 'processing';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'error':
      return 'error';
    case 'pending':
    default:
      return 'pending';
  }
};

export const StudyFilesUploadConfirmationModal: React.FC<
  StudyFilesUploadConfirmationModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  onCancelUpload,
  onRetryAllErrors,
  onRetryFile,
  onRemoveFile,
  files,
  isUploading = false,
  fileProgress = [],
  currentUploadingFileIndex,
}) => {
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const totalFiles = files.length;
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);

  // Convert files and progress to FileProgressItem format
  const fileProgressItems: FileProgressItem[] = useMemo(() => {
    return files.map((file, index) => {
      // Try to find progress by index first
      const progressData =
        fileProgress[index] ||
        fileProgress.find(
          fp => fp.file.name === file.name && fp.file.size === file.size
        );

      return {
        id: index,
        name: file.name,
        size: file.size,
        status: progressData
          ? mapUploadStatusToOperationStatus(progressData.status)
          : isUploading
            ? 'pending'
            : 'pending',
        progress: progressData?.progress || 0,
        error: progressData?.error,
      };
    });
  }, [files, fileProgress, isUploading]);

  // Calculate uploaded size in bytes based on progress
  const uploadedSize = useMemo(() => {
    return fileProgressItems.reduce((sum, fp) => {
      if (fp.size) {
        return sum + Math.round((fp.size * fp.progress) / 100);
      }
      return sum;
    }, 0);
  }, [fileProgressItems]);

  // Calculate overall progress based on file sizes (not file count)
  const overallProgress = useMemo(() => {
    if (totalSize === 0 || fileProgressItems.length === 0) return 0;
    return Math.round((uploadedSize / totalSize) * 100);
  }, [uploadedSize, totalSize, fileProgressItems.length]);

  // Check if upload is completed (all files are either completed or error, and upload has started)
  const hasStarted = fileProgressItems.some(fp => fp.status !== 'pending');
  const isCompleted =
    hasStarted &&
    fileProgressItems.length > 0 &&
    fileProgressItems.every(
      fp => fp.status === 'completed' || fp.status === 'error'
    );
  const hasErrors = fileProgressItems.some(fp => fp.status === 'error');
  const errorCount = fileProgressItems.filter(fp => fp.status === 'error').length;

  const displayFiles = useMemo(() => {
    if (isCompleted && hasErrors && showErrorsOnly) {
      return fileProgressItems.filter(fp => fp.status === 'error');
    }
    return fileProgressItems;
  }, [fileProgressItems, hasErrors, isCompleted, showErrorsOnly]);

  const displayProcessingIndex = useMemo(() => {
    if (
      typeof currentUploadingFileIndex !== 'number' ||
      !isCompleted ||
      !hasErrors ||
      !showErrorsOnly
    ) {
      return currentUploadingFileIndex;
    }
    const idx = displayFiles.findIndex(fp => fp.id === currentUploadingFileIndex);
    return idx >= 0 ? idx : undefined;
  }, [currentUploadingFileIndex, displayFiles, hasErrors, isCompleted, showErrorsOnly]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open: boolean) => !open && !isUploading && onClose()}
    >
      <Dialog.Content className="sm:max-w-[500px]">
        <DialogHeader
          startSlot={
            <Avatar radius="medium" size="medium">
              {isCompleted ? (
                hasErrors ? (
                  <IconAlertTriangle style={{ color: 'var(--orange-9)' }} />
                ) : (
                  <IconCheck style={{ color: 'var(--green-9)' }} />
                )
              ) : (
                <IconCloudUpload style={{ color: 'var(--blue-9)' }} />
              )}
            </Avatar>
          }
          title={
            isCompleted
              ? hasErrors
                ? 'Upload Completed with Errors'
                : 'Upload Completed Successfully'
              : 'Confirm Upload'
          }
          subtitle={
            isCompleted
              ? hasErrors
                ? `${errorCount} file(s) failed to upload`
                : `All ${fileProgressItems.filter(fp => fp.status === 'completed').length} file(s) uploaded successfully`
              : 'Confirm upload of selected files'
          }
        />
        <DialogBody>
          <Box mt="4">
            <Flex direction="column" gap="3">
              {/* Upload Summary */}
              <Card variant="classic" size="small" background={'var(--gray-2)'}>
                <Heading size="2xsmall" weight="medium" mb="2">
                  Upload Summary
                </Heading>
                <DataList
                  size="medium"
                  orientation="horizontal"
                  alignValues="end"
                >
                  <DataListItem
                    label="Files to upload:"
                    value={formatFileCount(totalFiles)}
                  />

                  <DataListItem
                    label="Total size:"
                    value={formatFileSize(totalSize)}
                  />
                </DataList>
                {(isUploading || isCompleted) && fileProgressItems.length > 0 && (
                  <Card
                    variant="classic"
                    size="xsmall"
                    color={isCompleted ? (hasErrors ? 'warning' : 'success') : 'info'}
                    mt="4"
                  >
                    <Flex
                      direction="column"
                      gap="2"
                      style={{ gridColumn: '1/-1' }}
                    >
                      <Flex justify="between" align="center">
                        <Text size="3xsmall">
                          {isCompleted
                            ? hasErrors
                              ? 'Upload Status'
                              : 'Upload Complete'
                            : 'Overall Progress'}
                          {!isCompleted && (
                            <Text as="span" size="inherit" weight="medium" ml="2">
                              {overallProgress}%
                            </Text>
                          )}
                        </Text>
                        <Text size="3xsmall">
                          {isCompleted
                            ? `${fileProgressItems.filter(fp => fp.status === 'completed').length} of ${files.length} files uploaded${hasErrors ? `, ${fileProgressItems.filter(fp => fp.status === 'error').length} failed` : ''}`
                            : `${formatFileSize(uploadedSize)} / ${formatFileSize(totalSize)}`}
                        </Text>
                      </Flex>
                      {!isCompleted && (
                        <Progress size="small" value={overallProgress} />
                      )}
                    </Flex>
                  </Card>
                )}
              </Card>

              {/* File List */}
              <Box mt="4">
                <Flex justify="between" align="center">
                  <Label>Selected Files:</Label>
                  <Flex align="center" gap="2">
                    {(isUploading || isCompleted) && fileProgressItems.length > 0 && (
                      <Text size="2xsmall" color="gray">
                        {fileProgressItems.filter(fp => fp.status === 'completed' || fp.status === 'error').length} / {files.length} files
                      </Text>
                    )}

                    {isCompleted && hasErrors && (
                      <>
                        <Button
                          size="small"
                          variant="surface"
                          color="default"
                          onClick={() => setShowErrorsOnly(v => !v)}
                          disabled={isUploading}
                          startIcon={
                            showErrorsOnly ? <IconList /> : <IconFilter />
                          }
                        >
                          {showErrorsOnly ? 'Show all' : 'Show errors only'}
                        </Button>

                        {onRetryAllErrors && (
                          <Button
                            size="small"
                            variant="classic"
                            color="warning"
                            onClick={onRetryAllErrors}
                            disabled={isUploading || errorCount === 0}
                            startIcon={<IconRefresh />}
                          >
                            Retry all errors
                          </Button>
                        )}
                      </>
                    )}
                  </Flex>
                </Flex>
                <FileProgressList
                  files={displayFiles}
                  currentProcessingIndex={displayProcessingIndex}
                  isProcessing={isUploading}
                  onRetryFile={
                    isCompleted && hasErrors && !isUploading && onRetryFile
                      ? (id) => {
                          if (typeof id === 'number') onRetryFile(id);
                        }
                      : undefined
                  }
                  onRemoveFile={
                    !isUploading && !isCompleted && onRemoveFile
                      ? (id) => {
                          if (typeof id === 'number') onRemoveFile(id);
                        }
                      : undefined
                  }
                />
              </Box>

              {/* Warning about potential overwrites */}
              {!isCompleted && (
                <Callout
                  icon={<IconAlertTriangle />}
                  title="Important Note"
                  variant="surface"
                >
                  If files with the same names already exist, new versions will
                  be created automatically. <br /> This action cannot be undone.
                </Callout>
              )}

              {isCompleted && hasErrors && (
                <Callout
                  icon={<IconAlertTriangle />}
                  title="Upload Completed with Errors"
                  variant="surface"
                  color="warning"
                >
                  Some files failed to upload. Please check the error messages
                  above and try uploading them again.
                </Callout>
              )}

              {isCompleted && !hasErrors && (
                <Callout
                  icon={<IconCheck />}
                  title="Upload Completed Successfully"
                  variant="surface"
                  color="success"
                >
                  All files have been uploaded successfully.
                </Callout>
              )}

              {!isUploading && !isCompleted && (
                <Text size="2xsmall">
                  Files will be uploaded immediately after confirmation. You'll
                  receive a notification when the upload is complete.
                </Text>
              )}
            </Flex>
          </Box>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outlined"
            color="secondary"
            onClick={isUploading ? onCancelUpload : onClose}
          >
            {isCompleted ? 'Close' : isUploading ? 'Cancel Upload' : 'Cancel'}
          </Button>
          {!isCompleted && (
            <Button
              variant="classic"
              color="primary"
              onClick={onConfirm}
              startIcon={<IconCloudUpload />}
              disabled={isUploading}
              loading={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          )}
        </DialogFooter>
      </Dialog.Content>
    </Dialog.Root>
  );
};

